import { useState } from 'react'
import EditItemModal from 'components/ItemModal/EditItemModal'
import Image from 'components/Image'
import { PencilSquareIcon } from '@heroicons/react/24/outline'

import { CartPage } from 'components/pages/CartPage/types/query'

type CartItemArgs = {
  item: NonNullable<CartPage['currentCart']>['price']['items'][0]
  cart: NonNullable<CartPage['currentCart']>
  index: number
  refetch: () => void
}

type Props = {
  cart: NonNullable<CartPage['currentCart']>
  refetch: () => void
}

function CartItem({ item, cart, index, refetch }: CartItemArgs) {
  const cartItem = cart.items[index]
  const [modalVisible, setModalVisible] = useState(false)

  return (
    <div className="flex flex-row justify-between py-4 group transition-all duration-200">
      <div className="w-4/5">
        <div
          aria-label={`Edit cart item ${item.name}`}
          className="flex items-start outline-none-mouse"
          onClick={() => setModalVisible(true)}
          onKeyDown={(e) => {
            if (e.keyCode === 13) {
              setModalVisible(true)
            }
          }}
          role="button"
          tabIndex={0}
        >
          <div className="mr-4 sm:mr-6">
            <Image
              alt="cart item"
              className="rounded-md"
              height={70}
              layout="fixed"
              src={cartItem.locationItem.item.image.medium}
              width={70}
            />
          </div>
          <div className="mr-2">
            <p className="sm:text-lg">
              <PencilSquareIcon className="text-orange-500 w-6 h-6 hover:text-orange-700 cursor-pointer inline mr-1" />
              <span className="group-hover:text-orange-900 font-bolder transition-all duration-200">
                {item.name}
              </span>
            </p>
            <div className="italic text-gray-600 text-sm sm:text-base">
              {cartItem &&
                cartItem.questions.map((question, qi) =>
                  question.answers.length > 0 ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={qi}>
                      <p>
                        {question.locationItemQuestion.text}:{' '}
                        {question.answers.map((a) => a.text).join(', ')}
                      </p>
                    </div>
                  ) : null
                )}
            </div>
          </div>
        </div>
      </div>
      <div className="w-1/5 flex flex-col items-end">
        <div className="text-right">
          <p>{item.total.formatted}</p>
          <p className="mt-1 text-sm text-gray-500">
            {item.quantity > 1 ? `${item.quantity} items` : ''}
          </p>
        </div>
      </div>

      {modalVisible && cartItem && (
        <EditItemModal
          cartItem={cartItem}
          cartItemIndex={index}
          cartUuid={cart.uuid!}
          closeModal={() => setModalVisible(false)}
          id={cartItem.locationItem.id}
          onUpdate={refetch}
          visible={modalVisible}
        />
      )}
    </div>
  )
}

export default function CartItems({ cart, refetch }: Props) {
  return (
    <div className="mb-4 flex flex-col divide-solid divide-gray-100 divide-y-2 border-solid border-t-2 border-b-2 border-gray-100">
      {cart.price.items.map((item, index) => (
        <CartItem
          // eslint-disable-next-line react/no-array-index-key
          key={`${item.name}${index}`}
          cart={cart}
          index={index}
          item={item}
          refetch={refetch}
        />
      ))}
    </div>
  )
}
